/**
 * Dashboards — pure layout + tile-query helpers.
 *
 * v1 dashboards deliberately ship the simple layout model (amendments §0):
 * tiles are a VERTICALLY ORDERED list rendered onto a 12-column grid — each
 * tile spans `w` columns and stacks in array order (y = index, x = 0). The
 * edit affordances are up/down reorder + an S/M/L size stepper, NOT 2D
 * drag-reflow, so the exact same interactions work on mobile.
 *
 * Everything here is pure and dependency-light so the reorder/resize/save
 * logic unit-tests without mounting a component.
 */

import type {
  LogsTileConfig,
  MetricRangeQuery,
  MetricTileConfig,
  MetricTileViz,
  QueryParams,
  ResolvedLogsTileConfig,
  ResolvedMetricTileConfig,
  ResolvedTile,
  Tile,
  TileType,
} from '../api.js';
import { filtersFromQuery } from '../composables/useFilters.js';
import { stepFor, unitFor, type ExplorationSpec } from './metric-exploration.js';
import type { MetricUnit } from './format-metric.js';
import type { VizKind } from '../ui/LgChart.vue';

/** Columns in the dashboard grid (desktop). Mobile collapses to one column. */
export const GRID_COLS = 12;

/** The three tile sizes (amendments §0). `w` is 12-col grid units; `h` is row
 * units the grid maps to a content height. */
export type SizeStep = 'S' | 'M' | 'L';
export const SIZE_STEPS: SizeStep[] = ['S', 'M', 'L'];
export const SIZE_LABEL: Record<SizeStep, string> = {
  S: 'Small (third width)',
  M: 'Medium (half width)',
  L: 'Large (full width)',
};
export const SIZE_DIMENSIONS: Record<SizeStep, { w: number; h: number }> = {
  S: { w: 4, h: 2 },
  M: { w: 6, h: 2 },
  L: { w: 12, h: 3 },
};

/** Pixels per `h` grid unit — a tile's content height is `h * TILE_ROW_PX`. */
export const TILE_ROW_PX = 120;

/** Live LOG tiles per dashboard share one poll and are capped (amendments §5). */
export const MAX_LIVE_LOGS_TILES = 4;

/** Server-enforced tile cap per dashboard (backend MAX_DASHBOARD_TILES). */
export const MAX_TILES = 24;

/** Default rows a logs tile shows (server caps at 500). */
export const DEFAULT_LOGS_TILE_LIMIT = 50;

/** Snap a stored width back to its size step (imports from other clients may
 * carry arbitrary w — nearest step wins so the stepper always shows a state). */
export function sizeOf(tile: Pick<Tile, 'w'>): SizeStep {
  if (tile.w <= SIZE_DIMENSIONS.S.w) return 'S';
  if (tile.w <= SIZE_DIMENSIONS.M.w) return 'M';
  return 'L';
}

/** Default size per tile type: metric charts start half-width, logs lists need
 * the full row to breathe. */
export function defaultSize(type: TileType): { w: number; h: number } {
  return type === 'metric' ? { ...SIZE_DIMENSIONS.M } : { ...SIZE_DIMENSIONS.L };
}

/** Client-generated tile id (§3.2). Falls back for older WebViews/jsdom. */
export function newTileId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tile-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Re-derive canonical positions from array order: y = index, x = 0. Keeps the
 * stored layout meaningful to any future 2D renderer without the v1 UI ever
 * editing x/y directly. */
export function normalizeTiles<T extends { x: number; y: number }>(tiles: T[]): T[] {
  return tiles.map((t, i) => (t.x === 0 && t.y === i ? t : { ...t, x: 0, y: i }));
}

/** Move a tile one slot up/down in array order. No-op at the edges or for an
 * unknown id. Always returns a normalized array. */
export function moveTile<T extends { id: string; x: number; y: number }>(
  tiles: T[],
  id: string,
  dir: 'up' | 'down',
): T[] {
  const from = tiles.findIndex((t) => t.id === id);
  if (from === -1) return normalizeTiles(tiles);
  const to = dir === 'up' ? from - 1 : from + 1;
  if (to < 0 || to >= tiles.length) return normalizeTiles(tiles);
  const next = [...tiles];
  [next[from], next[to]] = [next[to], next[from]];
  return normalizeTiles(next);
}

/** Apply a size step (S/M/L → w and h). Unknown id → unchanged (normalized). */
export function resizeTile<T extends { id: string; x: number; y: number; w: number; h: number }>(
  tiles: T[],
  id: string,
  size: SizeStep,
): T[] {
  const dims = SIZE_DIMENSIONS[size];
  return normalizeTiles(tiles.map((t) => (t.id === id ? { ...t, w: dims.w, h: dims.h } : t)));
}

export function removeTile<T extends { id: string; x: number; y: number }>(
  tiles: T[],
  id: string,
): T[] {
  return normalizeTiles(tiles.filter((t) => t.id !== id));
}

// --- Resolved → raw (what PUT /api/dashboards persists) ------------------------------------------

/**
 * Strip a resolved tile back to its storable form. `/resolved` dereferences a
 * saved source to `{kind:'inline', explorationId|filterSetId, spec|query}` for
 * rendering, but the backend layout validator only accepts the documented Tile
 * shapes — a saved ref must go back on the wire as `{kind:'saved', …}` (the id
 * on the resolved source is how we know it WAS a ref).
 */
export function rawTile(tile: ResolvedTile): Tile {
  const base = { id: tile.id, type: tile.type, x: tile.x, y: tile.y, w: tile.w, h: tile.h };
  const withTitle = tile.title !== undefined ? { ...base, title: tile.title } : base;
  if (tile.type === 'metric') {
    const config = tile.config as ResolvedMetricTileConfig;
    const source =
      'explorationId' in config.source
        ? ({ kind: 'saved', explorationId: config.source.explorationId } as const)
        : config.source;
    return { ...withTitle, config: { source, viz: config.viz } satisfies MetricTileConfig };
  }
  const config = tile.config as ResolvedLogsTileConfig;
  const source =
    'filterSetId' in config.source
      ? ({ kind: 'saved', filterSetId: config.source.filterSetId } as const)
      : config.source;
  const logsConfig: LogsTileConfig =
    config.limit !== undefined ? { source, limit: config.limit } : { source };
  return { ...withTitle, config: logsConfig };
}

export function rawLayout(tiles: ResolvedTile[]): { tiles: Tile[] } {
  return { tiles: normalizeTiles(tiles).map(rawTile) };
}

// --- Per-tile query building (the tile is a pure function of (config, window), §3.4) -------------

function specOf(config: ResolvedMetricTileConfig): ExplorationSpec | null {
  const src = config.source;
  if ('explorationId' in src) {
    return src.spec ? (src.spec as ExplorationSpec) : null;
  }
  return null;
}

/**
 * The §2.3 range-query body for a metric tile at a concrete clock window.
 * Returns `null` for an unresolved tile (deleted exploration) — nothing to ask.
 */
export function metricTileQuery(
  config: ResolvedMetricTileConfig,
  from: number,
  to: number,
): MetricRangeQuery | null {
  if (config.unresolved) return null;
  const src = config.source;
  if ('explorationId' in src) {
    const spec = specOf(config);
    if (!spec) return null;
    return {
      source: spec.source || undefined,
      name: spec.name,
      target: spec.target || undefined,
      matchers: spec.matchers?.length ? spec.matchers : undefined,
      from,
      to,
      step: stepFor(to - from),
      agg: spec.agg,
      groupBy: spec.splitBy ? [spec.splitBy] : undefined,
    };
  }
  const labels = src.labels ? Object.entries(src.labels) : [];
  return {
    name: src.metric,
    matchers: labels.length
      ? labels.map(([label, value]) => ({ label, op: 'eq' as const, value }))
      : undefined,
    from,
    to,
    step: stepFor(to - from),
    agg: src.agg,
  };
}

/** Metric-tile render metadata derived from the config (unit for the axis /
 * stat formatter, the split dimension for series naming, the raw metric name). */
export function metricTileMeta(config: ResolvedMetricTileConfig): {
  name: string;
  unit: MetricUnit;
  splitBy: string | null;
} {
  const src = config.source;
  if ('explorationId' in src) {
    const spec = specOf(config);
    if (!spec) return { name: 'metric', unit: 'none', splitBy: null };
    return { name: spec.name, unit: spec.yUnit ?? 'none', splitBy: spec.splitBy ?? null };
  }
  return {
    name: src.metric,
    unit: unitFor({ unit: null, name: src.metric }),
    splitBy: null,
  };
}

/** Map the tile viz to LgChart's kind (`stat` renders LgStatTile instead). */
export function chartTypeFor(viz: MetricTileViz): VizKind {
  return viz === 'stat' ? 'line' : viz;
}

/**
 * `/api/query` params for a logs tile at a concrete clock window. The tile's
 * saved/inline query string is the same URL shape `filtersToQuery` emits; its
 * own `range` (if any) is ignored — the dashboard clock owns the window (§3.4).
 * Returns `null` for an unresolved tile (deleted filter set).
 */
export function logsTileParams(
  config: ResolvedLogsTileConfig,
  from: number,
  to: number,
): QueryParams | null {
  if (config.unresolved) return null;
  const query = config.source.query;
  if (query === undefined) return null;
  const f = filtersFromQuery(query);
  const params: QueryParams = {
    from: String(from),
    to: String(to),
    limit: String(config.limit ?? DEFAULT_LOGS_TILE_LIMIT),
  };
  if (f.text.length) params.text = JSON.stringify(f.text);
  if (f.containers.length) params.containers = f.containers.join(',');
  if (f.stacks.length) params.stacks = f.stacks.join(',');
  if (f.levels.length) params.levels = f.levels.join(',');
  if (f.streams.length) params.streams = f.streams.join(',');
  if (f.attrs.length) params.attrs = JSON.stringify(f.attrs);
  return params;
}

// --- Display ---------------------------------------------------------------------------------

const PROM_SUFFIXES = ['_bucket', '_count', '_sum', '_total'];

/** Friendly title for a metric name (same treatment as the Explore chip):
 * strip the Prometheus suffix, break on `_`/`.`, sentence-case. */
export function humanizeMetricName(name: string): string {
  let base = name;
  for (const suf of PROM_SUFFIXES) {
    if (base.endsWith(suf)) {
      base = base.slice(0, -suf.length);
      break;
    }
  }
  const words = base.replace(/[._]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}

/** A tile's display title: the user's title, else a friendly derived one. */
export function tileTitle(tile: ResolvedTile): string {
  if (tile.title) return tile.title;
  if (tile.type === 'metric') {
    const config = tile.config as ResolvedMetricTileConfig;
    return humanizeMetricName(metricTileMeta(config).name);
  }
  return 'Logs';
}

/**
 * Which logs tiles keep polling while the dashboard is live: the first
 * MAX_LIVE_LOGS_TILES in layout order (amendments §5). Returns the set of
 * capped (paused) tile ids.
 */
export function cappedLogsTileIds(tiles: Array<Pick<Tile, 'id' | 'type'>>): Set<string> {
  const capped = new Set<string>();
  let liveCount = 0;
  for (const t of tiles) {
    if (t.type !== 'logs') continue;
    liveCount += 1;
    if (liveCount > MAX_LIVE_LOGS_TILES) capped.add(t.id);
  }
  return capped;
}
